import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { TrackingEvent } from '../../../AnalyticsContext/TrackingEvent';
import { useAnalytics } from '../../../AnalyticsContext/useAnalytics';
import { useRouter } from '../../../hooks/useRouter';
import { useServices } from '../../../services/Services';
import { supportedLanguages } from '../../../services/i18n/constants';
import { useMobile } from '../../../services/screenSize/ScreenSizeService';
import { PickerField } from '../../fields/PickerField/PickerField';
import { DropDownIcon } from '../../fields/PickerField/PickerField.styles';
import { ConfirmLanguageSelectionModal } from './ConfirmLanguageSelectionModal';
import {
  FloatingLanguageButton,
  LanguageGlobeIcon,
  LanguageTranslateIcon,
  NavBarLanguageButton,
} from './styles';

export const LanguagePicker = () => {
  const i18nService = useServices().i18n;
  const { i18n } = useTranslation();
  const language = i18nService.i18nextInstance.language;
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [modalShown, setModalShown] = useState(false);
  const { trackEvent } = useAnalytics();
  const isMobile = useMobile();
  const routeParams = useRouter()?.query;
  const langUI = routeParams?.langUI ? Boolean(routeParams?.langUI) : false;
  const { whiteLabelProps } = useTheme();
  const isCBP = whiteLabelProps?.governmentFlow ?? false;

  if (!langUI) return null;

  const shortName = supportedLanguages.find(lang => lang.code === language)?.short ?? '?';

  const languageOptions = supportedLanguages
    .filter(lang => lang.isVisible)
    .map(lang => ({
      label: lang.name,
      value: lang.code,
      dir: lang.direction,
    }));

  const onSelectionChanged = (langCode: string) => {
    setSelectedLanguage(langCode);

    const switchingToNonEnglishFromEnglish =
      langCode !== 'en' && i18nService.i18nextInstance.language === 'en';

    // Confirm legal agreements in English before switching languages, except
    // for Commonwealth practices (who have localised legal texts - so we don't
    // need to collect consent in English).
    if (switchingToNonEnglishFromEnglish && !isCBP) {
      setModalShown(true);
    } else {
      setLanguage(langCode);
    }
  };

  const onConfirmModal = () => {
    setLanguage(selectedLanguage);
  };

  const onCancelModal = () => {
    setSelectedLanguage(language);
    setModalShown(false);
  };

  const setLanguage = (lang: string) => {
    i18nService.changeLanguage(lang);

    trackEvent(TrackingEvent.LANGUAGE_CHANGE, { pageData: { language: lang } });
    setModalShown(false);
  };

  const pickerButton = isMobile ? (
    <FloatingLanguageButton>
      <LanguageTranslateIcon />
    </FloatingLanguageButton>
  ) : (
    <NavBarLanguageButton dir={i18n.dir()}>
      <LanguageGlobeIcon />
      {shortName}
      <DropDownIcon />
    </NavBarLanguageButton>
  );

  return (
    <>
      <ConfirmLanguageSelectionModal
        isOpen={modalShown}
        onConfirm={onConfirmModal}
        onCancel={onCancelModal}
      />
      <PickerField
        button={pickerButton}
        options={languageOptions}
        onSelect={onSelectionChanged}
        selectedOption={{ value: language, label: shortName }}
      />
    </>
  );
};
